import React from "react";
import { cn } from "../../lib/utils";
import { getMessageContentParts } from "../../utils/FormatUtils";
import { Message } from "./AssistantContext";

interface MessageBubbleProps {
  message: Message;
  isLastInGroup: boolean;
  renderMarkdown: (text: string) => React.ReactNode;
  renderMessageWithMentions: (text: string) => React.ReactNode;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isLastInGroup,
  renderMarkdown,
  renderMessageWithMentions
}) => {
  return (
    <div className={cn(
      "flex",
      message.role === "assistant" ? "justify-start" : "justify-end"
    )}>
      <div className={cn(
        "flex flex-col max-w-[85%] min-w-0",
        message.role === "assistant" ? "items-start" : "items-end ml-auto"
      )}>
        <div className="relative group w-full">
          {/* Bubble tail - only on last message of group */}
          {isLastInGroup && message.role !== "assistant" && (
            <div className="absolute -right-1 bottom-2 w-3 h-3 bg-maitai-green transform rotate-45 rounded-br-sm" />
          )}
          
          {/* Message bubble */}
          <div 
            className={cn(
              "relative px-4 py-2.5 shadow-sm",
              message.role === "assistant" 
                ? "bg-muted text-foreground rounded-2xl" 
                : cn(
                    "bg-maitai-green text-white",
                    isLastInGroup
                      ? "rounded-2xl rounded-br-sm"
                      : "rounded-2xl"
                  )
            )}
            style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
          >
            {(() => {
              const parts = getMessageContentParts(message.content);
              if (parts.length === 0) {
                return null;
              }
              return (
                <div className="space-y-2">
                  {parts.map((part, index) => {
                    if (part.type === "image") {
                      return (
                        <img
                          key={`${part.value}-${index}`}
                          src={part.value}
                          alt="Message attachment"
                          className="max-w-full rounded-md border border-border"
                        />
                      );
                    }
                    return message.role === "assistant" ? (
                      <div key={`text-${index}`}>{renderMarkdown(part.value)}</div>
                    ) : (
                      <p key={`text-${index}`} className="text-sm leading-relaxed whitespace-pre-wrap">
                        {renderMessageWithMentions(part.value)}
                      </p>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};
